import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useCreateLocation, useUpdateLocation } from '../hooks/useAdmin';
import type { Location } from '../services/locationsService';

const locationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  short_code: z
    .string()
    .min(2, 'Short code must be at least 2 characters')
    .max(10, 'Short code must be at most 10 characters'),
  is_active: z.boolean(),
});

type LocationFormData = z.infer<typeof locationSchema>;

interface LocationFormProps {
  isOpen: boolean;
  onClose: () => void;
  location?: Location;
}

export function LocationForm({ isOpen, onClose, location }: LocationFormProps) {
  const { showToast } = useToast();
  const isEditing = !!location;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: location
      ? {
          name: location.name,
          short_code: location.short_code,
          is_active: location.is_active,
        }
      : {
          name: '',
          short_code: '',
          is_active: true,
        },
  });

  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();

  useEffect(() => {
    if (isOpen) {
      if (location) {
        reset({
          name: location.name,
          short_code: location.short_code,
          is_active: location.is_active,
        });
      } else {
        reset({
          name: '',
          short_code: '',
          is_active: true,
        });
      }
    }
  }, [isOpen, location, reset]);

  const onSubmit = async (data: LocationFormData) => {
    try {
      if (isEditing && location) {
        await updateMutation.mutateAsync({
          id: location.id,
          ...data,
        });
        showToast('Location updated successfully', 'success');
      } else {
        await createMutation.mutateAsync(data);
        showToast('Location created successfully', 'success');
      }
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving location:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to save location',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Location' : 'Add New Location'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Location Name"
          required
          placeholder="e.g., Main Office"
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label="Short Code"
          required
          placeholder="e.g., MAIN"
          error={errors.short_code?.message}
          {...register('short_code')}
          helperText="2-10 characters, will be converted to uppercase"
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            {...register('is_active')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Active
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
